import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import CheckIcon from '@mui/icons-material/Check'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { useSortable } from '@/hooks/useSortable'

interface Agent {
  id: number
  username: string
  agent_type: 'human' | 'machine'
  admin: boolean
  slug: string
  email: string | null
  agent_provider: string | null
  created_at: string
}

interface Props {
  agents: Agent[]
}

const SORT_OPTIONS = [
  { value: 'username', label: 'Username' },
  { value: 'agent_type', label: 'Type' },
  { value: 'agent_provider', label: 'Provider' },
  { value: 'created_at', label: 'Created' },
]

export default function Index({ agents }: Props) {
  const { sorted, order, orderBy, handleSort } = useSortable(agents, 'username')
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null)

  function handleDelete() {
    if (deleteTarget) {
      router.delete(`/manage/agents/${deleteTarget.slug}`)
      setDeleteTarget(null)
    }
  }

  return (
    <SidebarLayout
      section="manage"
      title="Agents"
      action={
        <Button component={Link} href="/manage/agents/new" variant="contained">
          New agent
        </Button>
      }
    >
      {agents.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={orderBy}
              label="Sort by"
              onChange={e => handleSort(e.target.value)}
            >
              {SORT_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => handleSort(orderBy)}>
            {order === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No agents found.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {sorted.map((agent) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={agent.id}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1, mr: 1 }}>
                    {agent.username}
                  </Typography>
                  {agent.admin && <CheckIcon fontSize="small" color="success" />}
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  <Chip
                    label={agent.agent_type === 'human' ? 'Human' : 'Machine'}
                    size="small"
                    variant="outlined"
                    color={agent.agent_type === 'machine' ? 'secondary' : 'default'}
                  />
                  {agent.agent_type === 'machine' && agent.agent_provider && (
                    <Chip label={agent.agent_provider} size="small" variant="outlined" />
                  )}
                </Box>

                {agent.email && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {agent.email}
                  </Typography>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                  Created {new Date(agent.created_at).toLocaleDateString()}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button component={Link} href={`/manage/agents/${agent.slug}/edit`} size="small">
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => setDeleteTarget(agent)}>
                    Delete
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete agent"
        message={`Are you sure you want to delete agent '${deleteTarget?.username}'? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </SidebarLayout>
  )
}
